import React, { useState, useMemo, useCallback } from "react";
import { get } from "lodash";

import LazyTable, {
  LazyTableProps,
  TableSortType,
  LazyTableRow,
  MAX_ROWS_BEFORE_PAGINATION,
} from "components/LazyTable";
import { ContextMenuItem } from "types/common-types";

type Comparator = (a: LazyTableRow, b: LazyTableRow) => -1 | 0 | 1;

const descendingComparator = (
  a: LazyTableRow,
  b: LazyTableRow,
  accessor: string
): -1 | 0 | 1 => {
  let varA = get(a, accessor);
  let varB = get(b, accessor);

  if (varA === null || varA === undefined) return -1;
  if (varB === null || varB === undefined) return 1;

  if (Date.parse(varA) > 0) varA = Date.parse(varA);
  if (Date.parse(varB) > 0) varB = Date.parse(varB);

  if (typeof varA === "object") varA = varA.value;
  if (typeof varB === "object") varB = varB.value;

  if (typeof varA === "string") varA = varA.toUpperCase();
  if (typeof varB === "string") varB = varB.toUpperCase();

  if (varB < varA) return -1;
  if (varB > varA) return 1;
  return 0;
};

const getComparator = (order: TableSortType, accessor: string): Comparator => {
  return order === "desc"
    ? (a: LazyTableRow, b: LazyTableRow) => descendingComparator(a, b, accessor)
    : (a: LazyTableRow, b: LazyTableRow) =>
        -descendingComparator(a, b, accessor) as -1 | 0 | 1;
};

function stableSort(array: Array<LazyTableRow>, comparator: Comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]) as Array<
    [LazyTableRow, number]
  >;
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
}

export type SortableTableProps = {
  defaultOrderBy?: string;
  tableRowPerPage?: number;
  contextMenuItems?: ContextMenuItem[];
} & Omit<
  LazyTableProps,
  | "order"
  | "setOrder"
  | "orderBy"
  | "page"
  | "setPage"
  | "setOrderBy"
  | "rowsPerPage"
  | "setRowsPerPage"
  | "rowCount"
>;

const SortableTable = (props: SortableTableProps) => {
  const { defaultOrderBy, cells, rows, tableRowPerPage, contextMenuItems, ...rest } = props;

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState<string>(
    defaultOrderBy ?? cells[0].accessor
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    tableRowPerPage ?? MAX_ROWS_BEFORE_PAGINATION
  );

  const rowCount = (rows ?? []).length;
  const rowsForPage = useMemo(() => {
    const sorted = stableSort(rows ?? [], getComparator(order, orderBy));
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [order, orderBy, page, rows, rowsPerPage]);

  const setRowsPerPageAndResetPage = useCallback(
    (rrp: number) => {
      if (rrp !== rowsPerPage) setRowsPerPage(rrp);
      if (page !== 0) setPage(0);
    },
    [page, rowsPerPage]
  );

  return (
    <LazyTable
      {...{
        ...rest,
        rowCount,
        cells,
        rows: rowsForPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage: setRowsPerPageAndResetPage,
        contextMenuItems
      }}
    />
  );
};

export default React.memo(SortableTable);
 